import { useState } from 'react'
import { bulkSetDiscoverable, getUserPreference, setUserPreference } from '../../services/api'
import type { AggregatorProject } from '../../types'
import type { EntityTags } from '../../hooks/useAggregatorPreferences'
import BulkInviteDialog from './BulkInviteDialog'

interface BulkActionBarProps {
  selectedProjectIds: string[]
  projects: AggregatorProject[]
  entityTags: EntityTags
  activeAggregatorId: string | null
  onComplete: () => void
}

type Toast = { message: string; type: 'success' | 'error' }

export default function BulkActionBar({
  selectedProjectIds,
  projects,
  entityTags,
  activeAggregatorId,
  onComplete,
}: BulkActionBarProps) {
  const [loading, setLoading] = useState<string | null>(null)
  const [toast, setToast] = useState<Toast | null>(null)
  const [tagMode, setTagMode] = useState(false)
  const [tagText, setTagText] = useState('')
  const [showInviteDialog, setShowInviteDialog] = useState(false)

  const showToast = (message: string, type: Toast['type'] = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  const handleDiscoverable = async (discoverable: boolean) => {
    if (!activeAggregatorId) return
    setLoading(discoverable ? 'discoverable' : 'hidden')
    try {
      const result = await bulkSetDiscoverable(selectedProjectIds, discoverable, activeAggregatorId)
      const label = discoverable ? 'discoverable' : 'hidden'
      if (result.failed.length > 0) {
        showToast(`${result.updated} projects marked ${label} (${result.failed.length} failed)`)
      } else {
        showToast(`${result.updated} projects marked ${label}`)
      }
      onComplete()
    } catch {
      showToast('Action failed — please try again', 'error')
    } finally {
      setLoading(null)
    }
  }

  const exportCSV = () => {
    const selectedProjects = projects.filter((p) => selectedProjectIds.includes(p.project_id))
    const headers = ['Name', 'Entity', 'Domain', 'Stage', 'Status', 'Discoverable', 'Document Count', 'Date Added']
    const rows = selectedProjects.map((p) => [
      p.name ?? '',
      p.entity_name ?? '',
      entityTags[p.entity_id]?.domain ?? '',
      entityTags[p.entity_id]?.stage ?? '',
      p.status ?? '',
      p.discoverable ? 'Yes' : 'No',
      String(p.document_count ?? 0),
      p.created_at ? new Date(p.created_at).toLocaleDateString('en-AU') : '',
    ])
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `portfolio-export-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleSaveTag = async () => {
    if (!tagText.trim() || !activeAggregatorId) return
    setLoading('tag')
    try {
      const existing = await getUserPreference(`aggregator_project_tags:${activeAggregatorId}`).catch(() => null)
      const currentTags: Record<string, string[]> = (existing?.value as Record<string, string[]>) ?? {}
      const merged = { ...currentTags }
      for (const pid of selectedProjectIds) {
        merged[pid] = [...new Set([...(merged[pid] ?? []), tagText.trim()])]
      }
      await setUserPreference(`aggregator_project_tags:${activeAggregatorId}`, merged)
      showToast(`Tagged ${selectedProjectIds.length} projects with '${tagText.trim()}'`)
      setTagMode(false)
      setTagText('')
      onComplete()
    } catch {
      showToast('Failed to save tags', 'error')
    } finally {
      setLoading(null)
    }
  }

  return (
    <>
      <div
        data-testid="bulk-action-bar"
        className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg px-4 py-3 flex items-center gap-3 animate-slide-up"
        style={{ animation: 'slideUp 0.2s ease-out' }}
      >
        <span className="text-sm font-semibold text-gray-800 mr-2" data-testid="selection-count">
          {selectedProjectIds.length} selected
        </span>

        <button
          data-testid="bulk-mark-discoverable"
          onClick={() => handleDiscoverable(true)}
          disabled={loading !== null}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 disabled:opacity-50"
        >
          {loading === 'discoverable' ? 'Updating…' : 'Mark Discoverable'}
        </button>

        <button
          data-testid="bulk-mark-hidden"
          onClick={() => handleDiscoverable(false)}
          disabled={loading !== null}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 disabled:opacity-50"
        >
          {loading === 'hidden' ? 'Updating…' : 'Mark Hidden'}
        </button>

        <button
          data-testid="bulk-export-csv"
          onClick={exportCSV}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
        >
          Export CSV
        </button>

        {tagMode ? (
          <div className="flex items-center gap-2" data-testid="tag-input-row">
            <input
              data-testid="tag-input"
              type="text"
              value={tagText}
              onChange={(e) => setTagText(e.target.value)}
              placeholder="Tag name..."
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-iflow-500"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTag() }}
            />
            <button
              data-testid="tag-save"
              onClick={handleSaveTag}
              disabled={loading === 'tag' || !tagText.trim()}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-iflow-700 text-white hover:bg-iflow-800 disabled:opacity-50"
            >
              {loading === 'tag' ? 'Saving…' : 'Save'}
            </button>
            <button
              data-testid="tag-cancel"
              onClick={() => { setTagMode(false); setTagText('') }}
              className="px-3 py-1.5 text-sm font-medium rounded-lg text-gray-600 border border-gray-200 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            data-testid="bulk-tag"
            onClick={() => setTagMode(true)}
            className="px-3 py-1.5 text-sm font-medium rounded-lg bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
          >
            Tag
          </button>
        )}

        <button
          data-testid="bulk-invite"
          onClick={() => setShowInviteDialog(true)}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
        >
          Invite
        </button>

        {toast && (
          <div
            data-testid="bulk-toast"
            className={`ml-auto text-sm font-medium px-3 py-1.5 rounded-lg ${toast.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}
          >
            {toast.message}
          </div>
        )}
      </div>

      {showInviteDialog && (
        <BulkInviteDialog
          selectedProjectIds={selectedProjectIds}
          projects={projects}
          activeAggregatorId={activeAggregatorId}
          onClose={() => {
            setShowInviteDialog(false)
            onComplete()
          }}
        />
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  )
}
